import React, { useRef, useState } from 'react';
import { Card, CardBody } from 'react-bootstrap';
import { useAuth } from '../../../../contexts/AuthContext';
import { Filter, Undo, AlertTriangle, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

export const StockOnHand: React.FC = () => {

    const {fetchData, responseErrorMessage, showErrorAlert, handleCloseErrorAlert}  = useAuth();
    const stockOnHandReportQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"functions/SAP-SOH-REPORT/run2"}`;
    const [formatData, setFormatData] = useState({ date: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // State to hold preview data
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [downloadFile, setDownloadFile] = useState<Blob | null>(null);


    // Handles the change in date input
    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormatData(prev => ({ ...prev, date: e.target.value }));
      if (error) setError(null); //resetting error
    };

    // Resets the date filter
    const handleReset = () => {
        setFormatData({ date: '' });
        setPreviewData([]);
        setDownloadFile(null);
    };

  // Handles the report generation and error handling
  const handlePostFilter = async () => {
    setLoading(true);
    if (error) setError(null);
    const formData = { date: formatData.date };

      fetchData(
        stockOnHandReportQueryUrl,
        { method: "POST", body: JSON.stringify(formData) },
        (result: any) => {
            processStockOnHandExcel(result);
            if (error) setError(null); //resetting error
        },
        () => {
          setLoading(false); 

           // If the global error message was set, bring it into local scope
          if (showErrorAlert && responseErrorMessage) {
          setError(responseErrorMessage);

          // Optional: If you want to clear the global one too (not required)
          setTimeout(() => {
             showErrorAlert && handleCloseErrorAlert();
           }, 0);
          }

        }
      );
  };


  // Displays a loading spinner and message during report generation
  const ReportLoader: React.FC = () => (
    <div className='reports-loading-content'>
      <div className='reports-loading-wrapper'>
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        Your report is currently being generated. This process may take a few moments. Thank you for your patience.
      </div>
    </div>
  );

  // Displays an error message with an alert icon
  const ReportError: React.FC<{ message: string }> = ({ message }) => (
    <div className='reports-error-content'>
      <div className='reports-error-wrapper'>
        <AlertTriangle size={18} strokeWidth={2} />
        <span>{message}</span>
      </div>
    </div>
  );


  // Processes the Excel data and downloads it as a file
  const processStockOnHandExcel = async (result: any) => {

     // Step 1: Parse the Blob to preview
    const arrayBuffer = await result.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]]; // Use the first sheet
    const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    // Step 2: Set preview data to display
    setPreviewData(data);

    // Step 3: Save the Blob for later download
    setDownloadFile(result);  
 } 


 const renderPreviewTable = () => {

  const headerRow = previewData[1];
  const dataRows = previewData.slice(2, 101);

  return (
    <div className='rendering-report-preview-table-soh'>
      <div className='rendering-report-preview-header'>
        <div><strong>Stock On Hand - Previewing the first 100 records....</strong></div>
        <div className='d-flex gap-2'>
          <button
            className='btn btn-light'
            title='Download Stock On Hand Report'
            onClick={handleDownload}
            disabled={loading || !previewData.length}
            style={{ border: '1px solid #dbdad9' }}
            aria-label="Download report"
          >
            <Download />
          </button>
          <button
            className='btn btn-light'
            title='Reset Filter'
            onClick={handleReset}
            style={{ border: '1px solid #dbdad9' }}
            aria-label="Reset filter"
          >
            <Undo />
          </button>
        </div>
      </div>
      <div className='scrollable-table-container'>
        <div className='main-scroller'>
          <table className="table table-sm table-bordered" style={{ minWidth: '600px', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {headerRow.map((cell: any, index: number) => (
                  <th key={index}
                    style={{ 
                      position: 'sticky',
                      top: 0,
                      backgroundColor: '#343a40',
                      color: 'white',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      fontSize: '0.8rem',
                      padding: '6px 10px',
                      zIndex: 2,
                      border: '1px solid #dee2e6',
                    }}
                  >
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row: any, rowIndex: number) => (
                <tr key={rowIndex}>
                  {row.map((cell: any, cellIndex: number) => (
                    <td
                      key={cellIndex}
                      style={{
                        whiteSpace: 'nowrap',
                        fontSize: '0.8rem',
                        padding: '6px 10px',
                        border: '1px solid #dee2e6',
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


  
  const handleDownload = () => {
    if (downloadFile) {
      const formattedDate = formatData.date.replace(/-/g, '_') || 'unknown_date';
      const rawCompany = process.env.REACT_APP_COMPANY || 'unknown_company';
      const sanitizedCompany = rawCompany.replace(/-/g, '_');
      const filename = `${sanitizedCompany}_sales_analysis_${formattedDate}.xlsx`;
  
      const url = URL.createObjectURL(downloadFile);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
    }
  };
  
  return (
    <> 
    <Card>
       <Card.Header>   
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Card.Title>{`Stock On Hand`}</Card.Title>
            {previewData.length === 0 && (
            <div style={{ display: "flex", gap: "8px" }}>
            <button 
                 className='btn btn-light' 
                 title='Apply Filter' 
                 disabled={!formatData.date || loading}
                 style={{ border: '1px solid #dbdad9' }}
                 onClick={handlePostFilter} >
                 {loading ? (
                   <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                 ) : (
                   <Filter />
                 )}
            </button>
            <button className='btn btn-light' title='Reset Filter' onClick={handleReset} style={{ border: '1px solid #dbdad9' }}>
              <Undo />
            </button>
          </div>
          )}  
         </div>  
       </Card.Header>
       <CardBody>
          {previewData.length === 0 && (
          <div className='reports-container-wrapper'>
            <label className='reports-container-label'>
              <div className='reports-container-label-content'>
                <span>Start Date</span>
                <span className='required-tag'>* Required</span>
              </div>
              <input className='input-fields' type="date" name="date" value={formatData.date} onChange={handleDateChange} max={new Date().toISOString().split("T")[0]} />
            </label>
          </div>
          )}  
          {loading && <ReportLoader/>}
          {error && <ReportError message={error} />}
          {previewData.length > 0 && renderPreviewTable()}
       </CardBody>
     </Card>
   </>
  )
}

